/**
 * Dados da conta e entrada na fila.
 *
 * A carteira é lida direto do banco, protegida por RLS: o cliente só
 * enxerga a própria linha, e não tem permissão de escrita nenhuma. Já
 * entrar na fila passa pelo motor, porque parear e travar o escrow
 * acontecem na mesma transação e só a chave secreta pode disparar isso.
 */
import type { GameType } from './adapter.ts';
import { baseDaApi } from './presenca.ts';
import { supabase } from './sessao.ts';
import type { PerfilPublico } from './perfil.ts';

export interface CarteiraVirtual {
  readonly disponivelCents: number;
  readonly emDisputaCents: number;
}

export async function carregarCarteiraVirtual(userId: string): Promise<CarteiraVirtual | null> {
  const { data, error } = await supabase()
    .from('virtual_wallets')
    .select('balance_available_cents, balance_escrow_cents')
    .eq('user_id', userId)
    .maybeSingle();

  if (error || !data) return null;
  return {
    disponivelCents: Number(data.balance_available_cents),
    emDisputaCents: Number(data.balance_escrow_cents),
  };
}

/**
 * Estado da negociação de valor, como o banco devolve.
 *
 * `proposal` nulo é a mesa recém-formada, com os dois ainda em silêncio.
 * O saldo aqui é sempre o de quem pergunta: o do adversário não trafega.
 */
export interface EstadoAposta {
  readonly matchId: string;
  readonly gameType: GameType;
  readonly stakeMode: 'REAL' | 'VIRTUAL';
  readonly status: string;
  readonly stakeAgreed: boolean;
  readonly entryFeeCents: number;
  readonly rakeBps: number;
  readonly opponentName: string | null;
  /**
   * Nome e avatar dos dois lados da mesa. `opponent` é nulo enquanto a
   * partida não tem o segundo jogador — estado curto, mas real.
   */
  readonly opponent: PerfilPublico | null;
  readonly me: PerfilPublico;
  readonly balanceCents: number;
  readonly negotiationEndsAt: string;
  readonly limits: {
    readonly minCents: number;
    readonly maxCents: number;
    readonly maxRounds: number;
  };
  readonly proposal: {
    readonly entryFeeCents: number;
    readonly rakeBps: number;
    readonly byMe: boolean;
    readonly deadlineAt: string;
    readonly rounds: number;
    readonly expired: boolean;
  } | null;
}

export type ResultadoFila =
  | { readonly estado: 'pareado'; readonly matchId: string; readonly aposta: EstadoAposta | null }
  | { readonly estado: 'na-fila' }
  | { readonly estado: 'recusado'; readonly codigo: string; readonly mensagem: string }
  | { readonly estado: 'indisponivel' };

export type ResultadoAposta =
  | { readonly estado: 'ok'; readonly aposta: EstadoAposta }
  | { readonly estado: 'fechado'; readonly entryFeeCents: number }
  | { readonly estado: 'desfeito' }
  | { readonly estado: 'recusado'; readonly codigo: string; readonly mensagem: string }
  | { readonly estado: 'indisponivel' };

/** Códigos que o banco devolve como recusa de negócio, já em português. */
const MOTIVOS: Record<string, string> = {
  INSUFFICIENT_FUNDS: 'Saldo insuficiente para essa entrada.',
  OPPONENT_INSUFFICIENT_FUNDS: 'O adversário não cobre mais esse valor.',
  ALREADY_IN_MATCH: 'Você já está numa partida aberta.',
  SELF_EXCLUDED: 'Sua conta está em autoexclusão.',
  MINOR_BLOCKED: 'Apostas com valor real exigem 18 anos.',
  ACCOUNT_BLOCKED: 'Conta bloqueada. Fale com o suporte.',
  STAKE_ALREADY_AGREED: 'O valor já está fechado.',
  CANNOT_ACCEPT_OWN_PROPOSAL: 'Quem propõe não fecha sozinho: espere a resposta.',
  PROPOSAL_EXPIRED: 'A proposta venceu. Faça outra.',
  NEGOTIATION_EXPIRED: 'O tempo de combinar o valor acabou.',
  TOO_MANY_ROUNDS: 'Vocês já trocaram propostas demais. Procure outra mesa.',
  MATCH_NOT_NEGOTIABLE: 'Esta mesa não está mais aberta.',
  NO_PROPOSAL: 'Ninguém propôs valor ainda.',
  NOT_A_PLAYER: 'Você não está nesta partida.',
  MATCH_NOT_FOUND: 'Esta partida não existe mais.',
  INVALID_AMOUNT: 'Valor fora do permitido.',
};

/** Traduz a resposta de erro num motivo legível, sem inventar detalhe. */
async function recusa(resposta: Response): Promise<ResultadoAposta> {
  const corpo = (await resposta.json().catch(() => ({}))) as { error?: unknown };
  const codigo = typeof corpo.error === 'string' ? corpo.error : 'RECUSADO';
  return {
    estado: 'recusado',
    codigo,
    mensagem: MOTIVOS[codigo] ?? 'Não foi possível concluir.',
  };
}

/**
 * Entra na fila.
 *
 * `entryFeeCents` é só a sugestão que você leva para a mesa — a fila pareia
 * por jogo, não por valor. Quem não tem preferência entra sem ele.
 */
export async function entrarNaFila(
  token: string,
  gameType: GameType,
  entryFeeCents: number | null,
  stakeMode: 'REAL' | 'VIRTUAL' = 'VIRTUAL',
): Promise<ResultadoFila> {
  const base = baseDaApi();
  if (!base) return { estado: 'indisponivel' };

  let resposta: Response;
  try {
    resposta = await fetch(`${base}/api/matchmaking`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify({ gameType, entryFeeCents, stakeMode }),
    });
  } catch {
    return { estado: 'indisponivel' };
  }

  if (resposta.status === 200) {
    const corpo = (await resposta.json()) as { matchId?: unknown; stake?: unknown };
    return typeof corpo.matchId === 'string'
      ? {
          estado: 'pareado',
          matchId: corpo.matchId,
          aposta: (corpo.stake as EstadoAposta | undefined) ?? null,
        }
      : { estado: 'indisponivel' };
  }
  if (resposta.status === 202) return { estado: 'na-fila' };

  if (resposta.status === 409) {
    const corpo = (await resposta.json()) as { error?: unknown };
    const codigo = typeof corpo.error === 'string' ? corpo.error : 'RECUSADO';
    return {
      estado: 'recusado',
      codigo,
      mensagem: MOTIVOS[codigo] ?? 'Não foi possível entrar na fila.',
    };
  }

  return { estado: 'indisponivel' };
}

export type SituacaoJogador =
  | { readonly estado: 'pareado'; readonly matchId: string; readonly aposta: EstadoAposta | null }
  | { readonly estado: 'na-fila' }
  | { readonly estado: 'parado' }
  | { readonly estado: 'indisponivel' };

/**
 * Onde eu estou.
 *
 * É esta a pergunta que o lobby faz enquanto espera adversário. Perguntar
 * "me põe na fila" de novo devolveria `ALREADY_IN_MATCH` a quem acabou de
 * ser pareado — uma recusa exatamente no instante em que a resposta
 * deveria ser a boa notícia.
 */
export async function situacaoNaFila(token: string): Promise<SituacaoJogador> {
  const base = baseDaApi();
  if (!base) return { estado: 'indisponivel' };

  let resposta: Response;
  try {
    resposta = await fetch(`${base}/api/matchmaking`, {
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    return { estado: 'indisponivel' };
  }
  if (resposta.status !== 200) return { estado: 'indisponivel' };

  const corpo = (await resposta.json()) as {
    status?: unknown;
    matchId?: unknown;
    stake?: unknown;
  };

  if (corpo.status === 'MATCHED' && typeof corpo.matchId === 'string') {
    return {
      estado: 'pareado',
      matchId: corpo.matchId,
      aposta: (corpo.stake as EstadoAposta | undefined) ?? null,
    };
  }
  if (corpo.status === 'QUEUED') return { estado: 'na-fila' };
  return { estado: 'parado' };
}

/** Lê o estado da negociação de valor de uma partida já pareada. */
export async function lerAposta(token: string, matchId: string): Promise<ResultadoAposta> {
  const base = baseDaApi();
  if (!base) return { estado: 'indisponivel' };

  let resposta: Response;
  try {
    resposta = await fetch(`${base}/api/stake?matchId=${encodeURIComponent(matchId)}`, {
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    return { estado: 'indisponivel' };
  }

  if (resposta.status === 200) {
    return { estado: 'ok', aposta: (await resposta.json()) as EstadoAposta };
  }
  if (resposta.status >= 400 && resposta.status < 500) return recusa(resposta);
  return { estado: 'indisponivel' };
}

/** Propõe, aceita ou recusa um valor. */
async function acaoAposta(
  token: string,
  matchId: string,
  action: 'PROPOSE' | 'ACCEPT' | 'DECLINE',
  entryFeeCents?: number,
): Promise<ResultadoAposta> {
  const base = baseDaApi();
  if (!base) return { estado: 'indisponivel' };

  let resposta: Response;
  try {
    resposta = await fetch(`${base}/api/stake`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify({ matchId, action, entryFeeCents }),
    });
  } catch {
    return { estado: 'indisponivel' };
  }

  if (resposta.status === 200) {
    const corpo = (await resposta.json()) as {
      status?: unknown;
      stake?: unknown;
      entryFeeCents?: unknown;
    };
    if (corpo.status === 'AGREED' && typeof corpo.entryFeeCents === 'number') {
      return { estado: 'fechado', entryFeeCents: corpo.entryFeeCents };
    }
    if (corpo.stake) return { estado: 'ok', aposta: corpo.stake as EstadoAposta };
    // DECLINE não devolve estado nenhum: a mesa deixou de existir.
    return { estado: 'desfeito' };
  }

  if (resposta.status >= 400 && resposta.status < 500) return recusa(resposta);
  return { estado: 'indisponivel' };
}

export function proporValor(
  token: string,
  matchId: string,
  entryFeeCents: number,
): Promise<ResultadoAposta> {
  return acaoAposta(token, matchId, 'PROPOSE', entryFeeCents);
}

/**
 * Aceita o valor proposto.
 *
 * É este passo que move dinheiro: o servidor trava a entrada dos dois na
 * mesma transação. Ou os dois entram com o valor preso, ou nenhum entra.
 */
export function aceitarValor(token: string, matchId: string): Promise<ResultadoAposta> {
  return acaoAposta(token, matchId, 'ACCEPT');
}

export function recusarValor(token: string, matchId: string): Promise<ResultadoAposta> {
  return acaoAposta(token, matchId, 'DECLINE');
}

export async function sairDaFila(token: string): Promise<void> {
  const base = baseDaApi();
  if (!base) return;
  try {
    await fetch(`${base}/api/matchmaking`, {
      method: 'DELETE',
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    // Sair da fila é melhor-esforço: se falhar, a limpeza periódica do
    // banco remove a entrada antiga de qualquer jeito.
  }
}
